import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { cn } from "@fox1ck-org/ui";
import type { Lander } from "@gen/tracker/v1/lander_pb";

import { DASH, formatInt, formatPct } from "@/lib/format";
import { plural, useT } from "@/lib/i18n";
import type { Bag } from "@/lib/campaigns-table";

import { CodeChip, LanderTypeBadge, RiskBadge } from "./kinds";
import { LanderPicture, LanderSketch } from "./sketch";

/** One lander of the library with its period numbers. */
export interface LanderLine {
  id: string;
  lander: Lander;
  v: Bag | undefined;
  /** Live campaigns serving it; undefined while the numbers load. */
  campaigns: number | undefined;
}

/** «19.10 18:04» in the viewer's clock. Pure. */
export function shortStamp(iso: string): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The first `n` country codes as chips, the rest as «+3». */
export function Countries({ codes, n = 2 }: { codes: readonly string[]; n?: number }) {
  if (codes.length === 0) return <span className="text-ink-3">{DASH}</span>;
  return (
    <span className="flex items-center gap-1">
      {codes.slice(0, n).map((c) => (
        <CodeChip key={c} code={c} />
      ))}
      {codes.length > n ? <span className="text-xs text-muted-foreground">+{codes.length - n}</span> : null}
    </span>
  );
}

function CampaignsWord({ n }: { n: number | undefined }) {
  const t = useT();
  if (n === undefined) return <span className="text-ink-3">{DASH}</span>;
  if (n === 0) return <span className="text-ink-3">{t("landers.unused")}</span>;
  return <span>{t("landers.inCampaigns", { n, campaigns: plural(t("landers.word.campaignsIn"), n) })}</span>;
}

const num = (v: number | undefined) => <span className="font-mono tabular-nums">{formatInt(v)}</span>;
const pct = (v: number | undefined) => <span className="font-mono tabular-nums">{formatPct(v, { digits: 0 })}</span>;

/** The table's columns (board LanderList). Sortable where the server sorts: name, risk, changed. */
export function useLanderColumns(): ColumnDef<LanderLine, unknown>[] {
  const t = useT();
  return useMemo(
    () => [
          {
            id: "name",
            header: t("landers.col.name"),
            accessorFn: (r: LanderLine) => r.lander.name,
            size: 290,
            cell: ({ row }) => {
              const l = row.original.lander;
              return (
                <span className="flex w-67.5 min-w-0 items-center gap-2.5">
                  <span className="h-7.5 w-10.5 shrink-0 overflow-hidden rounded-sm border border-border">
                    <LanderSketch type={l.landerType} />
                  </span>
                  <span className="min-w-0">
                    <Link to={`/library/landers/${l.id}`} onClick={(e) => e.stopPropagation()} className="block truncate font-medium text-foreground hover:text-primary" title={l.name}>
                      {l.name}
                    </Link>
                    <span className="block truncate font-mono text-xs text-ink-3">{l.id.slice(0, 8)}</span>
                  </span>
                </span>
              );
            },
          },
          { id: "type", header: t("landers.col.type"), enableSorting: false, size: 120, cell: ({ row }) => <LanderTypeBadge type={row.original.lander.landerType} /> },
          { id: "lang", header: t("landers.col.lang"), enableSorting: false, size: 52, cell: ({ row }) => <span className="font-mono text-xs">{row.original.lander.language || DASH}</span> },
          { id: "countries", header: t("landers.col.countries"), enableSorting: false, size: 110, cell: ({ row }) => <Countries codes={row.original.lander.countryTargets} /> },
          {
            id: "version",
            header: t("landers.col.version"),
            enableSorting: false,
            size: 56,
            meta: { align: "right" },
            cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.lander.currentVersion ? `v${row.original.lander.currentVersion}` : DASH}</span>,
          },
          { id: "visits", header: t("landers.col.visits"), enableSorting: false, size: 76, meta: { align: "right" }, cell: ({ row }) => num(row.original.v?.visits) },
          { id: "lp_ctr", header: () => <span className="whitespace-nowrap">{t("landers.col.lpCtr")}</span>, enableSorting: false, size: 72, meta: { align: "right" }, cell: ({ row }) => pct(row.original.v?.lp_ctr) },
          { id: "leads", header: t("landers.col.leads"), enableSorting: false, size: 64, meta: { align: "right" }, cell: ({ row }) => num(row.original.v?.leads) },
          {
            id: "campaigns",
            header: t("landers.col.campaigns"),
            enableSorting: false,
            size: 88,
            meta: { align: "right" },
            cell: ({ row }) => {
              const n = row.original.campaigns;
              return n === 0 ? <span className="block max-w-20 truncate text-xs text-ink-3" title={t("landers.unused")}>{t("landers.unused")}</span> : num(n);
            },
          },
          { id: "risk_status", header: t("landers.col.risk"), size: 120, cell: ({ row }) => <RiskBadge risk={row.original.lander.riskStatus} /> },
          {
            id: "updated_at",
            header: t("landers.col.updated"),
            size: 112,
            meta: { align: "right" },
            cell: ({ row }) => <span className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">{shortStamp(row.original.lander.updatedAt)}</span>,
          },
    ],
    [t],
  );
}

/** Board LanderTiles: the page itself, what it is, and its period's three numbers. */
export function LanderTiles({ lines, onOpen }: { lines: LanderLine[]; onOpen: (id: string) => void }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(--spacing(68),1fr))] gap-3">
      {lines.map((line) => (
        <LanderTile key={line.id} line={line} onOpen={() => onOpen(line.id)} />
      ))}
    </div>
  );
}

function LanderTile({ line, onOpen }: { line: LanderLine; onOpen: () => void }) {
  const t = useT();
  const l = line.lander;
  const v = line.v;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card text-left transition-colors hover:border-border-strong focus-visible:outline-2 focus-visible:outline-ring"
    >
      <span className="flex h-7 shrink-0 items-center gap-1.5 bg-surface-2 px-2">
        <LanderTypeBadge type={l.landerType} />
        {l.language ? <CodeChip code={l.language} /> : null}
      </span>
      <span className="relative block h-32 w-full border-b border-border">
        <LanderPicture landerId={l.id} type={l.landerType} hasContent={l.hasContent} />
      </span>
      <span className="flex flex-col gap-2.5 px-3 pb-3 pt-2.5">
        <span className="min-w-0">
          <span className="block truncate font-medium text-foreground group-hover:text-primary">{l.name}</span>
          <span className="block truncate font-mono text-xs text-ink-3">
            {l.id.slice(0, 8)} · {l.currentVersion ? `v${l.currentVersion}` : t("landers.noVersion")} · {t("landers.changed", { at: shortStamp(l.updatedAt) })}
          </span>
        </span>
        <span className="grid grid-cols-3 gap-2 border-y border-border py-2">
          <TileNumber label={t("landers.col.visits")} value={formatInt(v?.visits)} />
          <TileNumber label={t("landers.col.lpCtr")} value={formatPct(v?.lp_ctr, { digits: 0 })} />
          <TileNumber label={t("landers.kpi.leads")} value={formatInt(v?.leads)} />
        </span>
        <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <RiskBadge risk={l.riskStatus} />
          <Countries codes={l.countryTargets} />
          <span className={cn("ml-auto truncate")}>
            <CampaignsWord n={line.campaigns} />
          </span>
        </span>
      </span>
    </button>
  );
}

function TileNumber({ label, value }: { label: string; value: string }) {
  return (
    <span className="min-w-0">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span className="block font-mono text-sm tabular-nums text-foreground">{value}</span>
    </span>
  );
}
